import { WORKSPACE_PADDING } from '@components/v2/constants';
import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import { Box, Skeleton, Stack, Theme } from '@mui/material';
import { ChevronDown, Maximize2, MessageSquarePlus, Minimize2, X } from 'lucide-react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../translation/TranslationContext';
import RachelChatHistoryModal from '../RachelChatHistoryModal/RachelChatHistoryModal';
import { useRachelHeader } from './useRachelHeader';

interface RachelHeaderProps {
  onNewConversation: () => void;
}

const CHAT_TITLE_SKELETON_WIDTH = 140;

const CHAT_TITLE_MAX_WIDTH_IN_PANEL = 220;

const PAGE_HEADER_BLEED = WORKSPACE_PADDING * 2;

const PAGE_HEADER_PADDING_LEFT = 1;

const PAGE_HEADER_PADDING_RIGHT = 1.25;

const getPageDividerSx = (theme: Theme) => ({
  marginX: theme.spacing(-PAGE_HEADER_BLEED),
  paddingLeft: theme.spacing(PAGE_HEADER_PADDING_LEFT),
  paddingRight: theme.spacing(PAGE_HEADER_PADDING_RIGHT),
  paddingBottom: theme.spacing(WORKSPACE_PADDING),
  borderBottom: `1px solid ${theme.palette.divider}`,
});

const RachelHeader = ({ onNewConversation }: RachelHeaderProps) => {
  const { translate } = useTranslation();
  const {
    isHistoryOpen,
    presentation,
    openHistory,
    closeHistory,
    toggleMaximize,
    isCollapseDisabled,
    close,
    hasConversation,
    activeChatTitle,
    isLoadingActiveChatTitle,
  } = useRachelHeader();
  const isDocked = presentation.mode === 'docked';
  const isOnPage = presentation.mode === 'page';
  const maximizeLabel = translate(
    presentation.isMaximized ? LangEnum.RACHEL_HEADER_COLLAPSE : LangEnum.RACHEL_HEADER_EXPAND,
  );
  const newConversationLabel = translate(LangEnum.RACHEL_NEW_CONVERSATION);
  const closeLabel = translate(LangEnum.RACHEL_HEADER_CLOSE);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      sx={(theme) => (isOnPage ? getPageDividerSx(theme) : {})}
    >
      <Button
        color="secondary"
        testId="rachel-chat-history-trigger"
        aria-label={isLoadingActiveChatTitle ? translate(LangEnum.GLOBAL_LOADING) : undefined}
        endIcon={<Icon icon={ChevronDown} size="md" />}
        onClick={openHistory}
        sx={(theme) => ({
          minWidth: 0,
          maxWidth: isOnPage ? 'none' : CHAT_TITLE_MAX_WIDTH_IN_PANEL,
          marginLeft: isDocked ? theme.spacing(-1.5) : 0,
          '& .MuiButton-endIcon': { flexShrink: 0 },
        })}
      >
        {isLoadingActiveChatTitle ? (
          <Skeleton variant="text" width={CHAT_TITLE_SKELETON_WIDTH} sx={{ fontSize: 'inherit' }} />
        ) : (
          <Box
            component="span"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {activeChatTitle ?? translate(LangEnum.RACHEL_UNTITLED_CHAT)}
          </Box>
        )}
      </Button>
      <RachelChatHistoryModal isOpen={isHistoryOpen} onClose={closeHistory} />
      <Box sx={{ flex: 1 }} />
      {!presentation.isOverlaid && hasConversation && (
        <Tooltip TooltipContent={newConversationLabel} tabIndex={-1}>
          <IconButton
            icon={MessageSquarePlus}
            testId="rachel-new-conversation"
            ariaLabel={newConversationLabel}
            onClick={onNewConversation}
          />
        </Tooltip>
      )}
      {presentation.canToggleMaximize && (
        <Tooltip TooltipContent={maximizeLabel} disabled={isCollapseDisabled} tabIndex={-1}>
          <IconButton
            icon={presentation.isMaximized ? Minimize2 : Maximize2}
            testId="rachel-toggle-maximize"
            ariaLabel={maximizeLabel}
            disabled={isCollapseDisabled}
            onClick={toggleMaximize}
          />
        </Tooltip>
      )}
      {!isOnPage && (
        <Tooltip TooltipContent={closeLabel} tabIndex={-1}>
          <IconButton icon={X} testId="rachel-close" ariaLabel={closeLabel} onClick={close} />
        </Tooltip>
      )}
    </Stack>
  );
};

export default RachelHeader;
